import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Nature as EcoIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Timeline as TimelineIcon,
  Visibility,
  Edit,
  Close,
  Save,
  Add,
  PlayArrow,
  Pause,
  Settings,
  TrendingUp,
  SwapVert,
} from '@mui/icons-material';

const WorkFlow = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedForStatus, setSelectedForStatus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    stageName: '',
    stageOrder: '',
    color: '#536DFE',
    description: '',
    isActive: true,
    duration: '',
    requiredFields: '',
    nextStages: '',
  });

  const sampleData = [
  { 
    id: 1, 
    stageName: 'Lead Generation', 
    stageOrder: 1, 
      color: '#536DFE',
    description: 'Initial customer inquiry and lead capture',
    isActive: true,
    duration: '1-2 days',
    requiredFields: ['Customer Name', 'Contact', 'Location'],
    nextStages: ['Site Survey'],
      totalLeads: 45,
      completedLeads: 38,
      successRate: 84.4,
  },
  { 
    id: 2, 
    stageName: 'Site Survey', 
    stageOrder: 2, 
    color: '#ff9800',
    description: 'Technical assessment and feasibility study',
    isActive: true,
    duration: '2-3 days',
    requiredFields: ['Site Address', 'Roof Area', 'Power Requirement'],
    nextStages: ['Quotation Preparation'],
      totalLeads: 38,
      completedLeads: 32,
      successRate: 84.2,
  },
  { 
    id: 3, 
    stageName: 'Quotation Preparation', 
    stageOrder: 3, 
      color: '#2196f3',
    description: 'Cost estimation and proposal creation',
    isActive: true,
    duration: '1-2 days',
    requiredFields: ['System Capacity', 'Product Selection', 'Pricing'],
    nextStages: ['Quotation Sent'],
      totalLeads: 32,
      completedLeads: 28,
      successRate: 87.5,
  },
  { 
    id: 4, 
    stageName: 'Quotation Sent', 
    stageOrder: 4, 
      color: '#9c27b0',
    description: 'Waiting for customer approval',
    isActive: true,
    duration: '3-7 days',
    requiredFields: ['Quotation Number', 'Validity Date'],
    nextStages: ['Order Confirmed', 'Negotiation'],
      totalLeads: 28,
      completedLeads: 18,
      successRate: 64.3,
  },
  { 
    id: 5, 
    stageName: 'Order Confirmed', 
    stageOrder: 5, 
    color: '#536DFE',
    description: 'Customer accepted and advance payment received',
    isActive: true,
    duration: '1 day',
    requiredFields: ['Order ID', 'Advance Amount', 'Agreement'],
    nextStages: ['Installation Planning'],
      totalLeads: 18,
      completedLeads: 18,
      successRate: 100,
  },
  { 
    id: 6, 
    stageName: 'Installation Planning', 
    stageOrder: 6, 
    color: '#ff5722',
    description: 'Resource allocation and scheduling',
    isActive: true,
    duration: '2-3 days',
    requiredFields: ['Team Assignment', 'Material List', 'Timeline'],
    nextStages: ['Installation In Progress'],
      totalLeads: 18,
      completedLeads: 15,
      successRate: 83.3,
  },
  { 
    id: 7, 
    stageName: 'Installation In Progress', 
    stageOrder: 7, 
    color: '#3f51b5',
    description: 'On-site installation and setup',
    isActive: true,
    duration: '5-10 days',
    requiredFields: ['Start Date', 'Team Lead', 'Daily Progress'],
    nextStages: ['Quality Check'],
      totalLeads: 15,
      completedLeads: 12,
      successRate: 80.0,
  },
  { 
    id: 8, 
    stageName: 'Quality Check', 
    stageOrder: 8, 
    color: '#f44336',
    description: 'Technical inspection and testing',
    isActive: true,
    duration: '1-2 days',
    requiredFields: ['QC Report', 'Test Results', 'Approver'],
    nextStages: ['Commissioning'],
      totalLeads: 12,
      completedLeads: 11,
      successRate: 91.7,
  },
  { 
    id: 9, 
    stageName: 'Commissioning', 
    stageOrder: 9, 
    color: '#009688',
    description: 'System activation and net metering',
    isActive: true,
    duration: '3-5 days',
    requiredFields: ['Grid Connection', 'Net Meter', 'Permissions'],
    nextStages: ['Handover'],
      totalLeads: 11,
      completedLeads: 10,
      successRate: 90.9,
  },
  { 
    id: 10, 
    stageName: 'Handover', 
    stageOrder: 10, 
    color: '#8bc34a',
    description: 'Final delivery to customer',
    isActive: true,
    duration: '1 day',
    requiredFields: ['Documentation', 'Training', 'Warranty Card'],
    nextStages: ['AMC / Maintenance'],
      totalLeads: 10,
      completedLeads: 10,
      successRate: 100,
  },
  { 
    id: 11, 
    stageName: 'AMC / Maintenance', 
    stageOrder: 11, 
    color: '#607d8b',
    description: 'Ongoing support and maintenance',
    isActive: true,
    duration: 'Ongoing',
    requiredFields: ['AMC Agreement', 'Schedule'],
    nextStages: [],
      totalLeads: 10,
      completedLeads: 8,
      successRate: 80.0,
    },
  ];

  const [data, setData] = useState(sampleData);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Determine if we're creating or updating
    if (selectedWorkflow) {
      handleUpdateWorkflowStage();
    } else {
      handleCreateWorkflowStage();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // New Workflow Stage handler
  const handleNewWorkflowStage = () => {
    console.log('Creating new workflow stage...');
    // Reset form data for new stage
    setEditFormData({
    stageName: '',
    stageOrder: '',
      color: '#536DFE',
    description: '',
    isActive: true,
    duration: '',
    requiredFields: '',
    nextStages: '',
  });
    setSelectedWorkflow(null);
    setEditDialogOpen(true);
  };

  // Create new workflow stage
  const handleCreateWorkflowStage = () => {
    if (!editFormData.stageName || !editFormData.stageOrder) {
      alert('Please fill in required fields: Stage Name and Stage Order');
      return;
    }

    // Create new stage object
    const newStage = {
      id: Date.now(), // Simple ID generation
      stageName: editFormData.stageName,
      stageOrder: parseInt(editFormData.stageOrder),
      color: editFormData.color,
      description: editFormData.description,
      isActive: editFormData.isActive,
      duration: editFormData.duration,
      requiredFields: editFormData.requiredFields ? editFormData.requiredFields.split(',').map(field => field.trim()) : [],
      nextStages: editFormData.nextStages ? editFormData.nextStages.split(',').map(stage => stage.trim()) : [],
      totalLeads: 0,
      completedLeads: 0,
      successRate: 0,
    };

    console.log('Creating new workflow stage:', newStage);
    
    // Here you would typically make an API call to save the new stage
    // For demo purposes, we'll just close the dialog and show success message
    setData(prev => [newStage, ...prev]);
    alert(`New workflow stage "${newStage.stageName}" created successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedWorkflow(null);
    setEditFormData({
      stageName: '',
      stageOrder: '',
      color: '#536DFE',
      description: '',
      isActive: true,
      duration: '',
      requiredFields: '',
      nextStages: '',
    });
  };

  // Update existing workflow stage
  const handleUpdateWorkflowStage = () => {
    if (!editFormData.stageName || !editFormData.stageOrder) {
      alert('Please fill in required fields: Stage Name and Stage Order');
      return;
    }

    const updatedStage = {
      ...selectedWorkflow,
      stageName: editFormData.stageName,
      stageOrder: parseInt(editFormData.stageOrder),
      color: editFormData.color,
      description: editFormData.description,
      isActive: editFormData.isActive,
      duration: editFormData.duration,
      requiredFields: editFormData.requiredFields ? editFormData.requiredFields.split(',').map(field => field.trim()) : [],
      nextStages: editFormData.nextStages ? editFormData.nextStages.split(',').map(stage => stage.trim()) : [],
    };

    console.log('Updating workflow stage:', updatedStage);
    
    // Here you would typically make an API call to update the stage
    // For demo purposes, we'll just close the dialog and show success message
    setData(prev => prev.map(i => i.id === updatedStage.id ? updatedStage : i));
    alert(`Workflow stage "${updatedStage.stageName}" updated successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedWorkflow(null);
    setEditFormData({
      stageName: '',
      stageOrder: '',
      color: '#536DFE',
      description: '',
      isActive: true,
      duration: '',
      requiredFields: '',
      nextStages: '',
    });
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      status: filters.status,
    });
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (workflow) => {
    setSelectedWorkflow(workflow);
    setViewDialogOpen(true);
  };

  const handleEdit = (workflow) => {
    setSelectedWorkflow(workflow);
    setEditFormData({
      stageName: workflow.stageName,
      stageOrder: workflow.stageOrder.toString(),
      color: workflow.color,
      description: workflow.description,
      isActive: workflow.isActive,
      duration: workflow.duration,
      requiredFields: workflow.requiredFields.join(', '),
      nextStages: workflow.nextStages.join(', '),
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedWorkflow(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedWorkflow(null);
    setEditFormData({
      stageName: '',
      stageOrder: '',
      color: '#536DFE',
      description: '',
      isActive: true,
      duration: '',
      requiredFields: '',
      nextStages: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return data;
    }

    let filteredData = [...data];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.stageName.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.requiredFields.some(field => field.toLowerCase().includes(searchLower)) ||
        item.nextStages.some(stage => stage.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => 
        filters.status === 'active' ? item.isActive : !item.isActive
      );
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.status, data]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalStages = filteredData.length;
  const activeStages = filteredData.filter(item => item.isActive).length;
  const totalLeads = filteredData.reduce((sum, item) => sum + item.totalLeads, 0);
  const completedLeads = filteredData.reduce((sum, item) => sum + item.completedLeads, 0);
  const averageSuccessRate = filteredData.length > 0 ? 
    (filteredData.reduce((sum, item) => sum + item.successRate, 0) / filteredData.length).toFixed(1) : 0;

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };
  const handleStatusToggle = (item) => {
    setSelectedForStatus(item);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!selectedForStatus) return;
    setData(prev => prev.map(i => i.id === selectedForStatus.id ? { ...i, isActive: !i.isActive } : i));
    setStatusConfirmDialogOpen(false);
    setSelectedForStatus(null);
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Green Energy Workflow Management
                </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage solar installation workflow stages and processes
                </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleNewWorkflowStage}
              sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Workflow Stage
            </Button>
          </Box>
      </Box>

      {/* Summary Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 1, p: 1.5, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={0.5}>
              <TimelineIcon sx={{ fontSize: 24, color: '#536DFE', mr: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                {totalStages}
              </Typography>
                  </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Total Stages
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 1, p: 1.5, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={0.5}>
              <CheckCircleIcon sx={{ fontSize: 24, color: '#536DFE', mr: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                {activeStages}
              </Typography>
                </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Active Stages
            </Typography>
            </Card>
          </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 1, p: 1.5, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={0.5}>
              <EcoIcon sx={{ fontSize: 24, color: '#ff9800', mr: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                {totalLeads}
                    </Typography>
                  </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Total Leads
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 1, p: 1.5, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={0.5}>
              <TrendingUp sx={{ fontSize: 24, color: '#2196f3', mr: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                {averageSuccessRate}%
              </Typography>
                </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Avg Success Rate
            </Typography>
            </Card>
        </Grid>
          </Grid>

      {/* Filters */}
      <Card sx={{ mb: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderRadius: 1 }}>
              <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
            Workflow Filters
                    </Typography>
          
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
              <InputLabel>Stage Status</InputLabel>
              <Select 
                label="Stage Status" 
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                }}
                sx={{
                  borderRadius: 1,
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              size="small"
              label="Search Workflow"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by stage name, description, or requirements..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchLoading ? (
                      <CircularProgress size={16} color="primary" />
                    ) : (
                      <SearchIcon fontSize="small" />
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ mr: -0.5 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{
                minWidth: 250,
                maxWidth: 350,
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#536DFE',
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  minWidth: 80,
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilters({
                    status: '',
                    searchTerm: '',
                  });
                  setSearchTerm('');
                  setHasSearched(false);
                  setSearchLoading(false);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  minWidth: 80,
                  '&:hover': {
                    borderColor: '#3F51B5',
                    backgroundColor: '#e8f5e8',
                  }
                }}
              >
                Clear All
              </Button>
                  </Box>
                </Box>

              </CardContent>
            </Card>

      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e8f5e8' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e8f5e8',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
                  </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching workflow stages...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} stage{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
                </Box>
              </CardContent>
            </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Workflow Stages
            </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Stage</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Description</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Duration</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Leads</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Success Rate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching workflow stages...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow 
                      key={row.id} 
                      hover
                    sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#e8f5e8 !important' }
                      }}
                    >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                              backgroundColor: row.color,
                          }}
                        />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.stageName}
                        </Typography>
                      </Box>
                    </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.description}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.duration}</TableCell>
                    <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.completedLeads}/{row.totalLeads}
                      </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Completed/Total
                          </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                          label={`${row.successRate}%`}
                        size="small" 
                          color={getSuccessRateColor(row.successRate)}
                          sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={row.isActive}
                            onChange={() => handleStatusToggle(row)}
                            color="success"
                            size="medium"
                          />
                        }
                        label={
                          <Chip
                            label={row.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={getStatusColor(row.isActive)}
                            sx={{ fontWeight: 600 }}
                          />
                        }
                        sx={{ m: 0 }}
                      />
                    </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                          <Tooltip title="Edit Stage">
                          <IconButton 
                            size="small" 
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No workflow stages found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog 
        open={statusConfirmDialogOpen} 
        onClose={() => setStatusConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {selectedForStatus?.isActive ? 'Deactivate Workflow Stage' : 'Activate Workflow Stage'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedForStatus?.isActive ? 'deactivate' : 'activate'} the stage <strong>"{selectedForStatus?.stageName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedForStatus?.isActive ? 'Deactivating this stage will mark it as inactive.' : 'Activating this stage will mark it as active.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setStatusConfirmDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={confirmStatusToggle} variant="contained" color={selectedForStatus?.isActive ? 'warning' : 'success'} sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>
            {selectedForStatus?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Details Dialog */}
        <Dialog 
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
          maxWidth="md"
          fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon />
            <Typography variant="h6" fontWeight="bold">
              Workflow Stage Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
          </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedWorkflow && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: selectedWorkflow.color, width: 48, height: 48 }}>
                  <EcoIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedWorkflow.stageName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stage {selectedWorkflow.stageOrder} • {selectedWorkflow.duration}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedWorkflow.isActive ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={getStatusColor(selectedWorkflow.isActive)}
                    />
                    <Chip 
                      label={`${selectedWorkflow.successRate}% Success`} 
                      size="small" 
                      color={getSuccessRateColor(selectedWorkflow.successRate)}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Stage Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineIcon color="primary" />
                        Stage Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Stage Name:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedWorkflow.stageName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Stage Order:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedWorkflow.stageOrder}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Duration:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedWorkflow.duration}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Description:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedWorkflow.description}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Performance Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="primary" />
                        Performance Metrics
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Total Leads:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedWorkflow.totalLeads}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Completed Leads:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedWorkflow.completedLeads}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Success Rate:</Typography>
                          <Chip 
                            label={`${selectedWorkflow.successRate}%`} 
                            size="small" 
                            color={getSuccessRateColor(selectedWorkflow.successRate)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <Chip 
                            label={selectedWorkflow.isActive ? 'Active' : 'Inactive'} 
                            size="small" 
                            color={getStatusColor(selectedWorkflow.isActive)}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Requirements and Next Stages */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Settings color="primary" />
                    Requirements & Next Stages
                  </Typography>
            <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Required Fields:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedWorkflow.requiredFields.map((field, index) => (
                          <Chip key={index} label={field} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Next Stages:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedWorkflow.nextStages.map((stage, index) => (
                          <Chip key={index} label={stage} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Workflow Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: selectedWorkflow ? '#ff9800' : '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedWorkflow ? <Edit /> : <Add />}
            <Typography variant="h6" fontWeight="bold">
              {selectedWorkflow ? 'Edit Workflow Stage' : 'New Workflow Stage'}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {selectedWorkflow ? `Edit workflow stage: ${selectedWorkflow.stageName}` : 'Create new workflow stage'}
            </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stage Name"
                  required
                    value={editFormData.stageName}
                    onChange={(e) => handleEditFormChange('stageName', e.target.value)}
                    variant="outlined"
                    size="small"
                    error={!editFormData.stageName}
                    helperText={!editFormData.stageName ? 'Stage name is required' : ''}
                />
              </Grid>
                <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stage Order"
                  type="number"
                  required
                    value={editFormData.stageOrder}
                    onChange={(e) => handleEditFormChange('stageOrder', e.target.value)}
                    variant="outlined"
                    size="small"
                    error={!editFormData.stageOrder}
                    helperText={!editFormData.stageOrder ? 'Stage order is required' : ''}
                />
              </Grid>
                <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Duration"
                    value={editFormData.duration}
                    onChange={(e) => handleEditFormChange('duration', e.target.value)}
                    variant="outlined"
                    size="small"
                />
              </Grid>
                <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                    value={editFormData.color}
                    onChange={(e) => handleEditFormChange('color', e.target.value)}
                    variant="outlined"
                    size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                    label="Description"
                    value={editFormData.description}
                    onChange={(e) => handleEditFormChange('description', e.target.value)}
                    variant="outlined"
                    size="small"
                  multiline
                  rows={2}
                />
              </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Required Fields (comma separated)"
                    value={editFormData.requiredFields}
                    onChange={(e) => handleEditFormChange('requiredFields', e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Customer Name, Contact, Location"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Next Stages (comma separated)"
                    value={editFormData.nextStages}
                    onChange={(e) => handleEditFormChange('nextStages', e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Site Survey, Quotation Preparation"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                        checked={editFormData.isActive}
                        onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                        color="success"
                    />
                  }
                  label="Active Stage"
                />
              </Grid>
            </Grid>

            {/* Read-only fields - only show when editing existing workflow */}
            {selectedWorkflow && (
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Performance Metrics (Read-only)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Total Leads:</strong> {selectedWorkflow.totalLeads}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Completed Leads:</strong> {selectedWorkflow.completedLeads}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Success Rate:</strong> {selectedWorkflow.successRate}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
          </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
              Cancel
            </Button>
            <Button 
            onClick={handleSaveEdit}
              variant="contained"
            startIcon={<Save />}
              sx={{ 
              backgroundColor: selectedWorkflow ? '#ff9800' : '#536DFE',
              '&:hover': { backgroundColor: selectedWorkflow ? '#f57c00' : '#3F51B5' }
              }}
            >
            {selectedWorkflow ? 'Save Changes' : 'Create Stage'}
            </Button>
          </DialogActions>
        </Dialog>
            </Box>
  );
};

export default WorkFlow;